/**
 * Compare's you-vs-them chart: two lines, one dex, over time.
 *
 * It plots the FACTS: both trainers' real dex totals, every observation in the
 * period, on one axis that holds all of them (see buildChartModel — it used to
 * zoom, and what the zoom hid is written up there). The y-axis is real counts,
 * so the higher line is the bigger dex and the space between them is the gap.
 *
 * Every point where a line moves is labelled ON the point with its +N, which is
 * how a single catch stays readable on a plot wide enough to hold two trainers
 * hundreds apart: the geometry cannot show it, and the number can.
 *
 * A completed trade between the two of you is marked with the species that
 * arrived, drawn as a small circular sprite on the line it lifted. That is the
 * one place the two stories meet: a point where both lines step up at once, for
 * a reason the chart can name.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { CenteredPillRow, DEX_VARIANT_META, type DexVariantKey } from "@/components/dexVariantVisuals";
import { CompareSprite } from "@/components/trade/CompareSprite";
import { speciesName } from "@/data/speciesNames";
import {
  axisTicks,
  buildChartModel,
  formatDayKey,
  formatDelta,
  friendLineColor,
  polylinePoints,
  type HistorySeriesPoint,
  type ChartPoint,
  type LineSummary,
  type TradeMarker,
} from "@/lib/dexHistoryChart";

/** The dexes the chart can plot. Mirrors the server's HISTORY_VARIANTS. */
const CHART_VARIANTS: DexVariantKey[] = [
  "pokemon", "shiny", "lucky", "perfect", "xxl", "xxs",
  "shadow", "purified", "mega", "gmax", "shundo", "shinyLucky",
];

/**
 * Week, fortnight, month.
 *
 * A quarter and a year were on offer and neither was worth the tap: this chart
 * is honed to the GAP between two trainers as it stands today, and over a year
 * that gap is a hairline crossing a wall of change labels. The interesting
 * question — "has one of us pulled ahead since the weekend?" — lives inside a
 * month, so that is the whole range now.
 */
const WINDOWS = [
  { key: "7", label: "Week" },
  { key: "14", label: "Fortnight" },
  { key: "30", label: "Month" },
] as const;

/** Opens on the week — the shortest window, and the one with the most detail in
 *  it now that the plot draws real totals rather than zooming. Same words and
 *  the same periods as the grind board below it. */
const DEFAULT_WINDOW = "7";

interface HistoryResponse {
  variant: string;
  days: number;
  today: string;
  mine: HistorySeriesPoint[];
  theirs: HistorySeriesPoint[];
  trades: TradeMarker[];
}

/** Plot geometry in viewBox units. Rendered responsively via preserveAspectRatio. */
const W = 320;
const H = 120;

/**
 * Room reserved to the LEFT of the plot for the y-axis labels.
 *
 * A real gutter rather than labels floated over the plot: a line that opens at
 * the bottom-left sits exactly where a floating bottom label would go, and a
 * number printed on a line is a number nobody trusts.
 */
const Y_GUTTER = 34;

/** Three down the x axis, two up the y — enough to give the plot a scale. */
const X_TICKS = 3;

/** Points worth a dot: any change, plus the first and last so a line is never
 *  a bare polyline with nothing drawn on it. */
function marks(points: ChartPoint[]): ChartPoint[] {
  if (points.length === 0) return [];
  const keep = new Map<string, ChartPoint>();
  keep.set(points[0].day, points[0]);
  keep.set(points[points.length - 1].day, points[points.length - 1]);
  for (const p of points) if (p.delta !== 0) keep.set(p.day, p);
  return Array.from(keep.values());
}

/**
 * How close two labels may sit before one of them has to go, as a fraction of
 * the plot's width. A "+72" is about 22 of 320 viewBox units wide.
 */
const LABEL_MIN_GAP = 0.09;

/**
 * EVERY change on the line gets a label, thinned only where two would print
 * through each other.
 *
 * It used to keep the last four and drop the rest, which on a month made the
 * chart quietly incomplete: days where something real was added carried no
 * number, so "what was added and when" — the only question this plot answers
 * now — went unanswered for most of the period.
 */
function labelled(points: ChartPoint[], color: string, who: "mine" | "theirs") {
  const recent = points.filter((p) => p.delta !== 0);
  // THIN OUT THE CROWD. Four changes on four consecutive days at the right-hand
  // end of a month rendered as "+4+72" and "+23+10" — labels printed straight
  // through each other, which is worse than not labelling them at all, because
  // an unreadable number still looks like a number. Walking backwards keeps the
  // most RECENT of a cluster, which is the one a reader is looking for.
  const kept: typeof recent = [];
  for (let i = recent.length - 1; i >= 0; i--) {
    const p = recent[i];
    if (kept.length > 0 && Math.abs(kept[kept.length - 1].x - p.x) < LABEL_MIN_GAP) continue;
    kept.push(p);
  }
  return kept.reverse().map((p) => ({ ...p, color, who }));
}

/**
 * Where a line's name tag sits, and what it says.
 *
 * WHOSE LINE IS THIS? Nothing on the plot answered that. The legend above named
 * the two totals but the lines themselves were anonymous, and on the Main dex
 * both were drawn in the same green — so the chart's own subject, "you against
 * them", could only be guessed at. Each line now carries its trainer's name at
 * its START, in that line's colour.
 *
 * A trainer with one reading has no line at all — a polyline with one vertex
 * draws nothing — so their tag says so rather than pointing at a bare dot.
 */
function tagText(name: string, s: LineSummary): string {
  return s.singlePoint ? `${name} · today` : name;
}

/** A y-axis label: a real dex total, because that is all a height means now. */
function axisLabel(value: number): string {
  return String(value);
}

/**
 * What a line gained over the window, for the legend beside its total.
 *
 * This used to ride on the tag, which is what made the tag wide enough to lie
 * across its own line: "Caprifunn +892" drawn along the bottom of the plot, over
 * the line it belonged to and over a "+287" label as well. The climb is a number
 * and the legend is where this card puts numbers; the tag only has to answer
 * "whose line is this", and a name is as short as that gets.
 */
function climbText(s: LineSummary): string | null {
  return s.climb === 0 ? null : formatDelta(s.climb);
}

/**
 * Place the two tags so they never sit on top of each other.
 *
 * Both lines usually start on the same day, at the left edge, and when the
 * trainers are close they start at almost the same height — so the tags would
 * overlap exactly. Whichever line starts higher takes the space above its
 * point; the other goes below its own.
 */
function tagPositions(mine: ChartPoint | undefined, theirs: ChartPoint | undefined) {
  const place = (p: ChartPoint | undefined, offsetPct: number) => {
    if (!p) return null;
    // Past the middle there is no room to the right of the point, so the tag
    // hangs off its left instead. A one-point line at today's edge is the case
    // this exists for.
    return {
      x: p.x,
      rightAnchored: p.x > 0.55,
      // The plot's own floor and ceiling are hard limits: a tag pushed outside
      // is a tag nobody reads.
      topPct: Math.min(92, Math.max(4, (1 - p.y) * 100 + offsetPct)),
    };
  };
  if (!mine || !theirs) return { mine: place(mine, -14), theirs: place(theirs, -14) };

  /**
   * Two trainers who open the period a few Pokémon apart start at almost the
   * same HEIGHT, and above-and-below would then put the two chips a few points
   * apart — on a 120px plot, less than one chip tall. So when the starts are
   * level the tags STACK, both above the point.
   */
  const level = Math.abs(mine.y - theirs.y) < 0.12;
  if (level) return { mine: place(mine, -30), theirs: place(theirs, -14) };
  // Otherwise each takes the space its own line is not using. Ties go to `mine`
  // above, which keeps the arrangement stable as the numbers move around rather
  // than flipping on every re-render.
  const mineHigher = mine.y >= theirs.y;
  return { mine: place(mine, mineHigher ? -14 : 6), theirs: place(theirs, mineHigher ? 6 : -14) };
}

export function DexHistoryChart({
  friendId,
  friendName,
  initialVariant = "pokemon",
}: {
  friendId: number;
  friendName: string;
  /** From `?category=` — the friend-row dex pills deep-link a dex. */
  initialVariant?: DexVariantKey;
}) {
  const [variant, setVariant] = useState<DexVariantKey>(initialVariant);
  const [days, setDays] = useState<string>(DEFAULT_WINDOW);

  const url = `/api/friends/${friendId}/dex-history?variant=${variant}&days=${days}`;
  const q = useQuery<HistoryResponse>({
    queryKey: [url],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const model = useMemo(
    () => buildChartModel(q.data?.mine ?? [], q.data?.theirs ?? [], q.data?.trades ?? []),
    [q.data],
  );

  const mineColor = "var(--accent-fill)";
  // Theirs is the FRIEND's colour, not the dex's — see friendLineColor. The dex
  // still owns the pill row above, which is where "which dex is this" belongs.
  const theirsColor = friendLineColor(friendId);
  const enoughToDraw = model.days.length >= 2;
  const tags = tagPositions(model.mine[0], model.theirs[0]);
  const ticks = axisTicks(model.days, X_TICKS);

  return (
    <section className="v2-neon-card-soft p-4 mt-4" data-testid="section-dex-history">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="v2-eyebrow" data-testid="eyebrow-dex-history">
          <span className="v2-eyebrow__dot" aria-hidden />
          Over time
        </span>
        <div className="flex gap-1 shrink-0">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setDays(w.key)}
              aria-pressed={days === w.key}
              data-testid={`dex-history-window-${w.key}`}
              className="rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{
                background: days === w.key ? "var(--accent-fill)" : "transparent",
                color: days === w.key ? "var(--text-on-accent)" : "var(--text-tertiary)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <CenteredPillRow
        testId="dex-history-variant-filter"
        ariaLabel="Choose which dex to chart"
        size="sm"
        activeKey={variant}
        onSelect={(k) => setVariant(k as DexVariantKey)}
        items={CHART_VARIANTS.map((v) => ({
          key: v,
          label: DEX_VARIANT_META[v].label,
          color: `hsl(${DEX_VARIANT_META[v].token})`,
          Icon: DEX_VARIANT_META[v].Icon,
        }))}
      />

      {/* The totals are a caption, deliberately: the AXIS is given over to the
          gap between the lines, so the absolute numbers have to be stated
          somewhere they can be read exactly. */}
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}>
          <span className="w-2.5 h-0.5 rounded-full" style={{ background: mineColor }} aria-hidden />
          You <span className="tabular-nums font-bold" style={{ color: "var(--text-ink-bright)" }} data-testid="dex-history-mine-total">{model.lastMine ?? "-"}</span>
          {/* What this line gained over the window — the thing the y-axis
              deliberately cannot show, since the plot is honed to today. */}
          {climbText(model.mineSummary) && (
            <span className="tabular-nums font-bold text-[10px]" style={{ color: mineColor }} data-testid="dex-history-mine-climb">
              {climbText(model.mineSummary)}
            </span>
          )}
        </span>
        {model.gap != null && (
          <span
            className="tabular-nums font-bold text-[10px] px-1.5 py-0.5 rounded-full"
            style={{
              color: model.gap === 0 ? "var(--text-tertiary)" : model.gap > 0 ? mineColor : theirsColor,
              background: "var(--surface-sunken)",
            }}
            data-testid="dex-history-gap"
          >
            {model.gap === 0 ? "level" : model.gap > 0 ? `you +${model.gap}` : `${friendName} +${Math.abs(model.gap)}`}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}>
          {climbText(model.theirsSummary) && (
            <span className="tabular-nums font-bold text-[10px]" style={{ color: theirsColor }} data-testid="dex-history-theirs-climb">
              {climbText(model.theirsSummary)}
            </span>
          )}
          <span className="tabular-nums font-bold" style={{ color: "var(--text-ink-bright)" }} data-testid="dex-history-theirs-total">{model.lastTheirs ?? "-"}</span>
          <span className="truncate max-w-[80px]">{friendName}</span>
          <span className="w-2.5 h-0.5 rounded-full" style={{ background: theirsColor }} aria-hidden />
        </span>
      </div>

      {!enoughToDraw ? (
        <p className="mt-3 text-[11.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="dex-history-empty">
          {q.isLoading
            ? "Loading history…"
            : q.isError
              ? "Couldn't load your history. Pull again in a moment."
              : `Not enough history yet. This fills in as you and ${friendName} keep scanning.`}
        </p>
      ) : (
        <div className="mt-2" data-testid="dex-history-plot">
          {/* One sentence, and it is the whole reading key: heights are totals,
              the numbers on the points are what was added that day. */}
          <p
            className="text-[10px] mb-1"
            style={{ color: "var(--text-tertiary)" }}
            data-testid="dex-history-caption"
          >
            Real totals from {formatDayKey(model.days[0])}. The higher line is ahead, and each
            number on a line is what was added that day.
          </p>
          {/*
            THE AXES. The plot had no scale on it at all — a shape, two lines
            and no way to say what any height or any horizontal position meant.
            Three marks across and two up is the least that fixes it: the ends
            of the period plus its middle, and what the floor and ceiling are
            worth. Reported on device 2026-08-13.

            The y labels get a real GUTTER rather than floating over the plot,
            because in relative mode both lines start at the bottom-left, which
            is exactly where a bottom label would sit — and a number printed on
            a line is a number nobody trusts.
          */}
          <div className="relative" style={{ height: H }}>
          <span
            className="absolute left-0 text-[9px] font-mono tabular-nums"
            style={{ top: 0, width: Y_GUTTER - 6, textAlign: "right", color: "var(--text-tertiary)" }}
            data-testid="dex-history-y-top"
          >
            {axisLabel(model.axis.top)}
          </span>
          <span
            className="absolute left-0 text-[9px] font-mono tabular-nums"
            style={{ bottom: 0, width: Y_GUTTER - 6, textAlign: "right", color: "var(--text-tertiary)" }}
            data-testid="dex-history-y-bottom"
          >
            {axisLabel(model.axis.bottom)}
          </span>

          <div className="absolute inset-y-0 right-0" style={{ left: Y_GUTTER }}>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height={H}
            preserveAspectRatio="none"
            role="img"
            aria-label={`${DEX_VARIANT_META[variant].label} dex over time: you ${model.lastMine}, ${friendName} ${model.lastTheirs}`}
          >
            {/* The posts themselves, under everything else. Hairlines, because
                the lines are the subject and a grid that competes with them has
                made the chart harder to read, not easier. */}
            {[0, H - 1].map((y) => (
              <line
                key={`gy-${y}`}
                x1={0}
                x2={W}
                y1={y + 0.5}
                y2={y + 0.5}
                stroke="var(--border-subtle)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {ticks.map((t) => (
              <line
                key={`gx-${t.day}`}
                x1={t.x * W}
                x2={t.x * W}
                y1={0}
                y2={H}
                stroke="var(--border-subtle)"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <polyline
              points={polylinePoints(model.theirs, W, H)}
              fill="none"
              stroke={theirsColor}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              data-testid="dex-history-line-theirs"
            />
            <polyline
              points={polylinePoints(model.mine, W, H)}
              fill="none"
              stroke={mineColor}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              data-testid="dex-history-line-mine"
            />
            {/* A dot where something HAPPENED, plus the line's own endpoint.
                Dotting every day turns a quiet fortnight into noise — but a
                series with ONE point is a polyline with one vertex, which draws
                literally nothing, so a trainer with a single observation had an
                invisible line. That is why "my line isn't showing" (device,
                2026-08-12): not a scale bug, a zero-length line. */}
            {marks(model.theirs).map((p) => (
              <circle key={`t-${p.day}`} cx={p.x * W} cy={(1 - p.y) * H} r={2.5} fill={theirsColor} />
            ))}
            {marks(model.mine).map((p) => (
              <circle key={`m-${p.day}`} cx={p.x * W} cy={(1 - p.y) * H} r={2.5} fill={mineColor} />
            ))}
            {/* A trainer with ONE observation has no line — a polyline with one
                vertex draws nothing — so their whole presence on the plot is a
                2.5px dot, which reads as a speck of dust rather than as a
                trainer. Ring it. */}
            {([[model.mineSummary, model.mine, mineColor, "mine"], [model.theirsSummary, model.theirs, theirsColor, "theirs"]] as const)
              .filter(([s, pts]) => s.singlePoint && pts.length === 1)
              .map(([, pts, color, who]) => (
                <circle
                  key={`solo-${who}`}
                  cx={pts[0].x * W}
                  cy={(1 - pts[0].y) * H}
                  r={5}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  data-testid={`dex-history-solo-${who}`}
                />
              ))}

            {/* Change labels, ON the point they belong to. They used to sit in a
                wrapped row under the chart, which said "+128" without saying
                WHEN — the one thing a point on a time axis is for. */}
            {/* MINE ABOVE ITS POINT, THEIRS BELOW. Both lines' labels used to
                go above, so where the two lines ran close together — which is
                most of the time, since that is what the zoom is for — a green
                +1 and a purple +72 landed on the same pixels. Splitting them by
                whose line they belong to separates them by a whole line-height
                without moving either away from the point it describes. */}
            {[...labelled(model.theirs, theirsColor, "theirs"), ...labelled(model.mine, mineColor, "mine")].map((p) => (
              <text
                key={`lbl-${p.who}-${p.day}`}
                x={Math.min(W - 14, Math.max(14, p.x * W))}
                y={
                  p.who === "mine"
                    ? Math.max(11, (1 - p.y) * H - 7)
                    : Math.min(H - 3, (1 - p.y) * H + 13)
                }
                textAnchor="middle"
                fontSize={10}
                fontWeight={800}
                fill={p.color}
                data-testid={`dex-history-delta-${p.who}-${p.day}`}
              >
                {formatDelta(p.delta)}
              </text>
            ))}
          </svg>

          {/* The name tags are HTML, not SVG text: the plot is drawn with
              `preserveAspectRatio="none"`, which stretches everything inside it
              horizontally to whatever width the card is — fine for a polyline,
              visibly wrong on a word. */}
          {([
            { key: "mine", name: "You", color: mineColor, pos: tags.mine, summary: model.mineSummary },
            { key: "theirs", name: friendName, color: theirsColor, pos: tags.theirs, summary: model.theirsSummary },
          ] as const).map(({ key, name, color, pos, summary }) =>
            pos == null ? null : (
              <span
                key={key}
                // A CHIP, not bare text. A tag sits at its line's first point,
                // which is exactly where that line and its +N labels are, so
                // some overlap is unavoidable — an opaque background is what
                // keeps the name readable through it instead of the two
                // striking each other out.
                className="absolute text-[10px] font-bold whitespace-nowrap pointer-events-none rounded-full px-1.5 py-px"
                data-testid={`dex-history-tag-${key}`}
                style={{
                  color,
                  background: "var(--surface-raised)",
                  border: `1px solid ${color}`,
                  top: `${pos.topPct}%`,
                  ...(pos.rightAnchored
                    ? { right: `${(1 - pos.x) * 100}%`, marginRight: 4 }
                    : { left: `${pos.x * 100}%`, marginLeft: 4 }),
                }}
              >
                {tagText(name, summary)}
              </span>
            ),
          )}
          </div>
          </div>

          {/* The dates, under the posts they belong to. HTML for the same
              reason the tags are: the plot stretches horizontally to the card's
              width, which is fine for a line and visibly wrong on a word. */}
          <div className="relative mt-1" style={{ marginLeft: Y_GUTTER, height: 12 }}>
            {ticks.map((t) => (
              <span
                key={`xl-${t.day}`}
                className="absolute text-[9px] font-mono whitespace-nowrap"
                style={{
                  color: "var(--text-tertiary)",
                  left: `${t.x * 100}%`,
                  // The end labels would hang off the card; anchor them inward.
                  transform:
                    t.x <= 0.01 ? "none" : t.x >= 0.99 ? "translateX(-100%)" : "translateX(-50%)",
                }}
                data-testid={`dex-history-x-tick-${t.day}`}
              >
                {formatDayKey(t.day)}
              </span>
            ))}
          </div>

          {/* Trade markers: the species that arrived, on the day it arrived. */}
          {model.markers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dex-history-trades">
              {model.markers.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-1 rounded-full pl-0.5 pr-2 py-0.5"
                  style={{ background: "var(--surface-raised)", border: `1px solid ${mineColor}` }}
                  data-testid={`dex-history-trade-${m.id}`}
                  title={`Traded ${speciesName(m.theirsSpeciesId)} for ${speciesName(m.mineSpeciesId)}`}
                >
                  <span
                    className="w-5 h-5 rounded-full overflow-clip inline-flex items-center justify-center shrink-0"
                    style={{ background: "var(--surface-sunken)" }}
                  >
                    <CompareSprite id={m.mineSpeciesId} shiny={false} size={18} />
                  </span>
                  <span className="text-[9.5px] font-semibold" style={{ color: "var(--text-secondary)" }}>
                    traded {formatDayKey(m.day)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
